import axios from 'axios';
import { clearAuthStorage } from './clearAuthStorage';
import { triggerUpgradePrompt } from '@/components/UpgradeRequiredModal';

const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : import.meta.env.DEV
    ? 'http://localhost:3000/api'
    : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // 402 Payment Required — backend's PlanFeatureGuard rejected because the
    // tenant's plan doesn't include the requested feature. Pop a contextual
    // upgrade modal globally so individual call sites don't have to handle
    // this themselves; they still receive the rejected promise.
    if (error.response?.status === 402) {
      const data = error.response.data || {};
      triggerUpgradePrompt({
        feature: data.feature,
        featureLabel: data.featureLabel,
        currentPlan: data.currentPlan,
        suggestedTier: data.suggestedTier,
        message: typeof data.message === 'string' ? data.message : undefined,
        subscriptionInactive: !!data.subscriptionInactive,
      });
      return Promise.reject(error);
    }

    // 403 EMAIL_NOT_VERIFIED — backend now enforces email verification on every
    // protected route. Route the user to the OTP page instead of showing a
    // broken screen (covers a session that lands on a protected page while
    // still unverified). The /verify-email page is public.
    if (error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/verify-email')) {
        window.location.assign('/verify-email');
      }
      return Promise.reject(error);
    }

    const isAuthRoute = original.url?.includes('/auth/login') || original.url?.includes('/auth/register') || original.url?.includes('/auth/forgot-password') || original.url?.includes('/auth/reset-password');
    if (error.response?.status === 401 && !original._retry && !isAuthRoute) {
      original._retry = true;
      try {
        const userId = localStorage.getItem('userId');
        const refreshToken = localStorage.getItem('refreshToken');
        if (!userId || !refreshToken) throw new Error('No refresh token');

        const { data } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { userId, refreshToken },
          { withCredentials: true },
        );
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        clearAuthStorage();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default api;
